// Currencies are stored as upper-case ISO 4217 codes, the Android format (Tamás, 2026-10-10, VLT-3049).
// Older records still hold the first apps' names (forint, euro, dollar, turkishLira) or iOS's lower-case
// codes (sar, aed), so everything read goes through normalizeCurrency and every write stores its result.

// The same list the apps offer.
export const CURRENCIES = ['AED', 'BGN', 'CHF', 'CZK', 'DKK', 'EUR', 'GBP', 'HUF', 'NOK', 'PLN', 'RON', 'RSD', 'RUB', 'SAR', 'SEK', 'TRY', 'UAH', 'USD']

const LEGACY_NAMES: { [name: string]: string } = {
  forint: 'HUF',
  euro: 'EUR',
  dollar: 'USD',
  turkishlira: 'TRY',
}

export function normalizeCurrency(currency?: string | null): string {
  const value = (currency ?? '').trim()
  return LEGACY_NAMES[value.toLowerCase()] ?? value.toUpperCase()
}

// Language to currency, per the "Penznemek / Currencies" Confluence page, which mirrors the iOS
// Currency.defaultCurrency(for:) (VLT-2064). Anything not listed is EUR.
const CURRENCY_BY_LANGUAGE: { [language: string]: string } = {
  ar: 'AED', cs: 'CZK', da: 'DKK', hu: 'HUF', no: 'NOK',
  pl: 'PLN', ro: 'RON', ru: 'RUB', sv: 'SEK', tr: 'TRY', uk: 'UAH',
}

export function defaultCurrencyForLanguage(language: string): string {
  return CURRENCY_BY_LANGUAGE[language] ?? 'EUR'
}

// The picker's choices with the current currency first and not repeated further down. A code outside
// the list still leads, so a record is never silently switched to another currency.
export function currencyOptions(current: string): string[] {
  return [current, ...CURRENCIES.filter(currency => currency !== current)]
}

// What people type for a price: "70", "70.1", "70,10", "0,20", "1 000". Spaces are dropped and a comma
// counts as the decimal separator. Anything that is not one non-negative number ("", ".", "70,1,2")
// gives null, so the field can say so instead of saving a different number.
export function parsePrice(input: string): number | null {
  const text = input.replace(/\s/g, '').replace(/,/g, '.')
  if (!/^(\d+\.?\d*|\.\d+)$/.test(text)) return null
  return parseFloat(text)
}

// Western digits in every language: only the separators follow the interface language.
const numberFormat = (language: string, min: number, max: number, useGrouping = true) =>
  new Intl.NumberFormat(language, { minimumFractionDigits: min, maximumFractionDigits: max, useGrouping, numberingSystem: 'latn' })

// A stored price as the text the edit field starts from, in the interface language's decimal separator.
export function priceInputText(value: number, language: string): string {
  return numberFormat(language, 0, 10, false).format(value)
}

// A forint price keeps its fillér (70,1 Ft/kWh is a real tariff) but shows no ",00"; other currencies
// always show two decimals.
export function formatPrice(value: number, currency: string, language: string): string {
  return numberFormat(language, currency === 'HUF' ? 0 : 2, 2).format(value)
}

// Costs are whole forints, and two decimals in every other currency.
export function formatCost(value: number, currency: string, language: string): string {
  const digits = currency === 'HUF' ? 0 : 2
  return numberFormat(language, digits, digits).format(value)
}
